/**
 * Lazy facade over `@telegram-apps/sdk-react` actions used across screens
 * and hooks. Goals:
 *   - Plain-web visitors never download the SDK chunk (~50-100kB).
 *   - In TMA, SDK loads once on first action call and is reused.
 *
 * Each method lazy-imports the SDK module via a cached Promise on first
 * use; the SDK's own `.isAvailable()` checks short-circuit gracefully
 * outside Telegram, giving fall-throughs to `window.open` etc. Web call
 * sites are gated by `useCapability(...)` upstream — when that gate is
 * false the methods here are not reached.
 *
 * Note: methods are async because the SDK import is async. Call sites
 * that previously did `if (sdk.x.isAvailable()) sdk.x(...)` should now
 * either `await tmaActions.x(...)` or fire-and-forget.
 */

type SdkModule = typeof import('@telegram-apps/sdk-react');

let sdkPromise: Promise<SdkModule | null> | null = null;
let initPromise: Promise<void> | null = null;

function loadSdk(): Promise<SdkModule | null> {
  if (sdkPromise) return sdkPromise;
  if (typeof window === 'undefined') {
    sdkPromise = Promise.resolve(null);
    return sdkPromise;
  }
  sdkPromise = import('@telegram-apps/sdk-react');
  return sdkPromise;
}

/**
 * Lazy-load the SDK AND ensure init() has been called. Action methods
 * with `.isAvailable()` checks (invoice.open, viewport, etc.) rely on
 * init() having registered the bridge — without it, isAvailable
 * returns false even inside Telegram. The TMA shell (runtime/tma/TmaApp)
 * also calls init() in a useEffect, but that effect can race with a user
 * tapping a payment method, so we defensively re-run it here. SDK init() is
 * idempotent: it returns a cleanup function and short-circuits on
 * subsequent calls when the bridge is already registered.
 */
async function ensureSdk(): Promise<SdkModule | null> {
  const sdk = await loadSdk();
  if (!sdk) return null;
  if (!initPromise) {
    initPromise = (async () => {
      try {
        sdk.init();
      } catch (err) {
        console.error('[tmaActions] init failed', err);
      }
    })();
  }
  await initPromise;
  return sdk;
}

export type {
  ShowPopupOptions,
  ShowPopupOptionsButton,
} from '@telegram-apps/sdk-react';

export const tmaActions = {
  async openLink(url: string, options?: { tryInstantView?: boolean }): Promise<void> {
    const sdk = await loadSdk();
    if (sdk && sdk.openLink.isAvailable()) {
      sdk.openLink(url, options);
      return;
    }
    if (typeof window !== 'undefined') {
      window.open(url, '_blank');
    }
  },

  async close(): Promise<void> {
    const sdk = await loadSdk();
    if (sdk && sdk.closeMiniApp.isAvailable()) {
      sdk.closeMiniApp();
    }
  },

  async openTelegramLink(url: string): Promise<void> {
    const sdk = await loadSdk();
    if (sdk && sdk.openTelegramLink.isAvailable()) {
      sdk.openTelegramLink(url);
      return;
    }
    if (typeof window !== 'undefined') {
      window.open(url, '_blank');
    }
  },

  async shareMessage(msgId: string | number, callback?: (status: 'sent' | 'cancelled') => void): Promise<void> {
    const sdk = await loadSdk();
    if (!sdk || !sdk.shareMessage.isAvailable()) {
      callback?.('cancelled');
      return;
    }
    try {
      await sdk.shareMessage(String(msgId));
      callback?.('sent');
    } catch (err: any) {
      if (err?.message?.includes('ERR_CANCELLED') || err?.type === 'ERR_CANCELLED') {
        callback?.('cancelled');
        return;
      }
      throw err;
    }
  },

  async shareURL(url: string, text?: string): Promise<void> {
    const sdk = await loadSdk();
    if (sdk && sdk.shareURL.isAvailable()) {
      sdk.shareURL(url, text);
    }
  },

  invoice: {
    async isAvailable(): Promise<boolean> {
      const sdk = await ensureSdk();
      return !!sdk && sdk.invoice.open.isAvailable();
    },
    async open(slugOrUrl: string, type?: 'url'): Promise<'paid' | 'cancelled' | 'failed' | 'pending'> {
      const sdk = await ensureSdk();
      if (!sdk || !sdk.invoice.open.isAvailable()) {
        return 'cancelled';
      }
      const status =
        type === 'url'
          ? await sdk.invoice.open(slugOrUrl.replace('://telegram.me/', '://t.me/'), 'url')
          : await sdk.invoice.open(slugOrUrl);
      return status as 'paid' | 'cancelled' | 'failed' | 'pending';
    },
  },

  /**
   * Telegram-native share availability (used to decide between native
   * share-message UI vs fallback flows like t.me/share/url, navigator.share,
   * or clipboard). Returns false on plain web without loading the SDK.
   */
  async isShareMessageAvailable(): Promise<boolean> {
    const sdk = await loadSdk();
    return !!sdk && sdk.shareMessage.isAvailable();
  },
};

export const popup = {
  async open(
    options: import('@telegram-apps/sdk-react').ShowPopupOptions,
  ): Promise<string | null> {
    const sdk = await loadSdk();
    if (!sdk || !sdk.popup.show.isAvailable()) return null;
    return await sdk.popup.show(options);
  },
};
